import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { CircularProgress } from "@mui/material";
import Swal from "sweetalert2";

import theme from "components/foundations/themes";
import QuestionReviewForm from "components/modules/question/QuestionReviewForm";
import { useFindQuestionById } from "api/question/useFindQuestionById";
import { useFindAllSkills } from "api/common/skills/useFindAllSkills";
import { useChangeValidationStatus } from "api/question/useChangeValidationStatus";
import MasterPage from "components/modules/masterpage";

const QuestionReviewer = () => {
  const navigate = useNavigate();
  const { question_id: questionId } = useParams();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { question, isLoading: isLoadingQuestion } = useFindQuestionById(questionId, true);
  const { skills, isLoading: isLoadingSkills } = useFindAllSkills(true);
  const { changeValidationStatus } = useChangeValidationStatus(questionId);

  const handleApprove = async () => {
    try {
      setIsSubmitting(true);
      await changeValidationStatus(true);
      
      Swal.fire({
        title: "Question Approved",
        text: "The question has been successfully approved and validated.",
        icon: "success",
        confirmButtonText: "Continue",
        confirmButtonColor: theme.palette.primary.main,
      }).then(() => {
        navigate(`/thebai/question-details/${questionId}`);
      });
    } catch (error) {
      Swal.fire({
        title: "Error",
        text: "There was an error approving the question. Please try again.",
        icon: "error",
        confirmButtonText: "Got it!",
        confirmButtonColor: theme.palette.primary.main,
      });
      console.error("Error approving question:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReject = async () => {
    // Show confirmation dialog for rejection
    const result = await Swal.fire({
      title: "Reject Question",
      text: "Are you sure you want to reject this question? This will mark it as not validated.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, reject",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#d33",
      cancelButtonColor: theme.palette.primary.main,
    });

    if (result.isConfirmed) {
      try {
        setIsSubmitting(true);
        await changeValidationStatus(false);
        
        Swal.fire({
          title: "Question Rejected",
          text: "The question has been rejected and marked as not validated.",
          icon: "info",
          confirmButtonText: "Continue",
          confirmButtonColor: theme.palette.primary.main,
        }).then(() => {
          navigate(`/thebai/question-details/${questionId}`);
        });
      } catch (error) {
        Swal.fire({
          title: "Error",
          text: "There was an error rejecting the question. Please try again.",
          icon: "error",
          confirmButtonText: "Got it!",
          confirmButtonColor: theme.palette.primary.main,
        });
        console.error("Error rejecting question:", error);
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  return (
    <MasterPage>
      {isLoadingQuestion || isLoadingSkills ? (
        <CircularProgress />
      ) : (
        <QuestionReviewForm
          title="Question Review"
          subtitle="Review and approve this question"
          question={question}
          skills={skills}
          onApprove={handleApprove}
          onReject={handleReject}
          isLoading={isSubmitting}
        />
      )}
    </MasterPage>
  );
};

export default QuestionReviewer;
